// Seller onboarding board. MOCK-ONLY: the gateway stores an onboarding status
// per seller profile, but has no board and no assignee ownership — both are
// proposed here.
//
// State is module-level and mutable so drag-and-drop and assignment survive
// between queries within a session; a reload resets it.
import { sleep } from "@/lib/utils";

import type { Api, ApplicationAssignee, ApplicationRemark, ApplicationSla, SellerApplication } from "../types";
import { ApiError } from "../errors";
import { crmBoardExtras } from "./application-crm";

const ASSIGNEES: ApplicationAssignee[] = [
	{ id: "mgr_orlova", name: "Ирина Орлова", initials: "ИО" },
	{ id: "mgr_petrov", name: "Дмитрий Петров", initials: "ДП" },
	{ id: "mgr_sokolova", name: "Мария Соколова", initials: "МС" },
];

const daysAgo = (days: number) => {
	const date = new Date();
	date.setDate(date.getDate() - days);
	return date.toISOString();
};

const event = (message: string, days: number, automated = false) => ({
	id: `evt_${Math.random().toString(36).slice(2, 10)}`,
	at: daysAgo(days),
	message,
	automated,
});

type StoredApplication = Omit<SellerApplication, "sla">;

// Офлайн-заглушка шлюза: сроки считает «сервер» по тем же правилам, что
// services/applications.ts (applicationSla), — консоль их только показывает.
const MSK_OFFSET = 3 * 3_600_000;
const DAY = 86_400_000;
const CLOSED = ["active", "rejected", "lost"];
const slaOf = (a: StoredApplication): ApplicationSla => {
	const day = (ms: number) => Math.floor((ms + MSK_OFFSET) / DAY);
	const days = Math.max(0, day(Date.now()) - day(new Date(a.statusChangedAt).getTime()));
	const closed = CLOSED.includes(a.status);
	const priority = closed ? "low" : days >= 7 ? "critical" : days >= 3 ? "high" : days >= 2 ? "normal" : "low";
	const alerts: ApplicationSla["alerts"] = [];
	if (!closed) {
		if (!a.assignee) alerts.push({ kind: "unassigned", severity: priority === "critical" ? "error" : "warning" });
		if (days >= 4) alerts.push({ kind: "stale", severity: days >= 7 ? "error" : "warning" });
	}
	return {
		daysInStage: days,
		slaDays: 3,
		overdue: !closed && days >= 3,
		daysLeft: closed ? null : Math.max(0, 3 - days),
		priority,
		alerts,
		severity: alerts.some((x) => x.severity === "error") ? "error" : alerts.length ? "warning" : null,
	};
};
const out = (a: StoredApplication): SellerApplication => ({ ...a, history: [...a.history], sla: slaOf(a) });

let applications: StoredApplication[] = [
	{
		id: "app_0001",
		phone: "+79214451208",
		shopName: "Гончарная Мастерская",
		contactName: "Ольга Ким",
		city: "Санкт-Петербург",
		categories: ["Керамика"],
		status: "new",
		assignee: null,
		submittedAt: daysAgo(1),
		statusChangedAt: daysAgo(1),
		priority: "normal",
		history: [event("Заявка создана", 1)],
	},
	{
		id: "app_0002",
		phone: "+79126003371",
		email: "vlasov@serebro-kamen.ru",
		inn: "667012345678",
		shopName: "Серебро и Камень",
		contactName: "Артём Власов",
		city: "Екатеринбург",
		categories: ["Украшения"],
		status: "new",
		assignee: null,
		submittedAt: daysAgo(4),
		statusChangedAt: daysAgo(4),
		priority: "high",
		note: "Крупный ассортимент, просит ускорить проверку.",
		history: [event("Заявка создана", 4)],
	},
	{
		id: "app_0003",
		phone: "+79203415519",
		email: "info@lnyanoy-dom.ru",
		inn: "3702123456",
		shopName: "Льняной Дом",
		contactName: "Наталья Гущина",
		city: "Иваново",
		categories: ["Текстиль", "Декор"],
		status: "contacted",
		assignee: ASSIGNEES[0] ?? null,
		submittedAt: daysAgo(6),
		statusChangedAt: daysAgo(5),
		priority: "normal",
		history: [event("Заявка создана", 6), event("Назначена Ирина Орлова", 5, true)],
	},
	{
		id: "app_0004",
		phone: "+79172280463",
		shopName: "Вязаный Мир",
		contactName: "Елена Дроздова",
		city: "Казань",
		categories: ["Аксессуары"],
		status: "contacted",
		assignee: ASSIGNEES[1] ?? null,
		submittedAt: daysAgo(9),
		statusChangedAt: daysAgo(8),
		priority: "low",
		history: [event("Заявка создана", 9)],
	},
	{
		id: "app_0005",
		phone: "+79137702145",
		email: "pavel@derevo-smola.ru",
		userId: "usr_seller_derevo",
		shopName: "Дерево и Смола",
		contactName: "Павел Ершов",
		city: "Новосибирск",
		categories: ["Декор"],
		status: "filling",
		assignee: ASSIGNEES[0] ?? null,
		submittedAt: daysAgo(12),
		statusChangedAt: daysAgo(3),
		priority: "normal",
		history: [event("Заявка создана", 12), event("Анкета заполнена", 3)],
	},
	{
		id: "app_0006",
		phone: "+79031186290",
		shopName: "Бумажная Птица",
		contactName: "Софья Лебедева",
		city: "Москва",
		categories: ["Декор", "Украшения"],
		status: "filling",
		assignee: null,
		submittedAt: daysAgo(15),
		statusChangedAt: daysAgo(7),
		priority: "normal",
		note: "Не приложены документы ИП.",
		history: [event("Заявка создана", 15)],
	},
	{
		id: "app_0007",
		phone: "+79106754012",
		inn: "332901234561",
		shopName: "Стеклодув",
		contactName: "Игорь Мельник",
		city: "Владимир",
		categories: ["Декор"],
		status: "moderation",
		assignee: ASSIGNEES[2] ?? null,
		submittedAt: daysAgo(18),
		statusChangedAt: daysAgo(2),
		priority: "high",
		history: [
			event("Заявка создана", 18),
			event("Отправлена на модерацию", 2),
			event("Назначена Мария Соколова", 2),
		],
	},
	{
		id: "app_0008",
		phone: "+79184027735",
		shopName: "Терра Керамика",
		contactName: "Анна Родина",
		city: "Краснодар",
		categories: ["Керамика"],
		status: "moderation",
		assignee: ASSIGNEES[2] ?? null,
		submittedAt: daysAgo(21),
		statusChangedAt: daysAgo(6),
		priority: "normal",
		history: [event("Заявка создана", 21), event("Отправлена на модерацию", 6)],
	},
	{
		id: "app_0009",
		phone: "+79115032864",
		email: "anna@masterskaya-anny.ru",
		userId: "usr_seller_anna",
		shopName: "Мастерская Анны",
		contactName: "Анна Мастерова",
		city: "Санкт-Петербург",
		categories: ["Украшения", "Аксессуары"],
		status: "active",
		assignee: ASSIGNEES[0] ?? null,
		submittedAt: daysAgo(30),
		statusChangedAt: daysAgo(11),
		priority: "normal",
		history: [event("Заявка создана", 30), event("Одобрена, магазин создан", 11, true)],
	},
	{
		id: "app_0010",
		phone: "+79200169347",
		shopName: "Нитка",
		contactName: "Вера Соловьёва",
		city: "Нижний Новгород",
		categories: ["Текстиль"],
		status: "active",
		assignee: ASSIGNEES[1] ?? null,
		submittedAt: daysAgo(34),
		statusChangedAt: daysAgo(20),
		priority: "low",
		history: [event("Заявка создана", 34), event("Одобрена, магазин создан", 20, true)],
	},
	{
		id: "app_0011",
		phone: "+79273901582",
		shopName: "Кожа и Нить",
		contactName: "Роман Гаврилов",
		city: "Самара",
		categories: ["Аксессуары"],
		status: "rejected",
		assignee: ASSIGNEES[2] ?? null,
		submittedAt: daysAgo(26),
		statusChangedAt: daysAgo(16),
		priority: "normal",
		note: "Перепродажа фабричного товара.",
		history: [event("Заявка создана", 26), event("Отклонена: не ручная работа", 16)],
	},
	// Весенняя заявка того же бизнеса, что app_0002: на ней видно предупреждение о дубле.
	{
		id: "app_0012",
		phone: "+79126003371",
		shopName: "Серебро & Камень",
		contactName: "Артём Власов",
		city: "Екатеринбург",
		categories: ["Украшения"],
		status: "lost",
		assignee: ASSIGNEES[1] ?? null,
		submittedAt: daysAgo(120),
		statusChangedAt: daysAgo(100),
		priority: "low",
		history: [event("Заявка создана", 120), event("Не вышел на связь", 100)],
	},
];

/** Заявка, заведённая через CRM-мок (вручную или загрузкой базы), — сразу на доске. */
export const mockInsertApplication = (application: StoredApplication) => {
	applications = [application, ...applications];
};

/** Правка основных полей из карточки CRM — доска показывает то же, что карточка. */
export const mockPatchApplication = (id: string, patch: Partial<StoredApplication>) => {
	const application = applications.find((entry) => entry.id === id);
	if (application) Object.assign(application, patch);
};

const find = (id: string) => {
	const application = applications.find((entry) => entry.id === id);
	if (!application) throw new ApiError("Application not found", 404, "NOT_FOUND");
	return application;
};

export const applications_api: Api["applications"] = {
	async board() {
		await sleep(250);
		// Hand back copies so a component can never mutate the store by accident.
		// Поля CRM (следующий шаг, теги, источник) — из мока CRM, как их добавит шлюз.
		return {
			applications: applications.map((application) => ({ ...out(application), ...crmBoardExtras(application.id) })),
			assignees: [...ASSIGNEES],
		};
	},

	async move(id, status) {
		await sleep(150);
		const application = find(id);
		if (application.status === status) return out(application);

		application.status = status;
		application.statusChangedAt = new Date().toISOString();
		application.history = [
			{
				id: `evt_${Math.random().toString(36).slice(2, 10)}`,
				at: new Date().toISOString(),
				message: `Перемещена в «${status}»`,
				automated: false,
			},
			...application.history,
		];
		return out(application);
	},

	async assign(id, assigneeId) {
		await sleep(150);
		const application = find(id);
		const assignee = assigneeId ? (ASSIGNEES.find((entry) => entry.id === assigneeId) ?? null) : null;
		application.assignee = assignee;
		application.history = [
			{
				id: `evt_${Math.random().toString(36).slice(2, 10)}`,
				at: new Date().toISOString(),
				message: assignee ? `Ответственный: ${assignee.name}` : "Ответственный снят",
				automated: false,
			},
			...application.history,
		];
		return out(application);
	},

	async createAccount(id, input) {
		await sleep(200);
		const application = find(id);
		if (application.userId) throw new Error("У этой заявки уже есть кабинет продавца");
		const userId = `usr_${Math.random().toString(36).slice(2, 10)}`;
		const sessionId = `inv_${Math.random().toString(36).slice(2, 10)}`;
		application.userId = userId;
		application.email = input.email;
		if (input.inn) application.inn = input.inn;
		if (input.shopName) application.shopName = input.shopName;
		application.status = "registered";
		application.statusChangedAt = new Date().toISOString();
		application.history = [
			{
				id: `evt_${Math.random().toString(36).slice(2, 10)}`,
				at: new Date().toISOString(),
				message: `Менеджер создал кабинет продавца (${input.email}) и отправил приглашение`,
				automated: false,
			},
			...application.history,
		];
		return {
			applicationId: id,
			userId,
			email: input.email,
			inviteUrl: `https://seller.osmix.ru/invite/${sessionId}`,
			inviteExpiresAt: new Date(Date.now() + 7 * 24 * 3600_000).toISOString(),
			smsSent: true,
		};
	},

	async rework(id, input) {
		await sleep(150);
		const application = find(id);
		application.status = "rework";
		application.statusChangedAt = new Date().toISOString();
		application.history = [
			{
				id: `evt_${Math.random().toString(36).slice(2, 10)}`,
				at: new Date().toISOString(),
				message: input.docType
					? `Секция на доработку (${input.docType}): ${input.message}`
					: `На доработку: ${input.message}`,
				automated: false,
			},
			...application.history,
		];
		const remark: ApplicationRemark = {
			id: `arm_${Math.random().toString(36).slice(2, 10)}`,
			applicationId: id,
			docType: input.docType ?? null,
			message: input.message,
			deadline: input.deadline ?? null,
			createdAt: new Date().toISOString(),
			resolvedAt: null,
		};
		return { application: out(application), remark };
	},
};
